import { defineStore } from 'pinia'
import { useQuery, useMutation } from '@tanstack/vue-query'
import { useAuthStore } from './authStore'
import { storeToRefs } from 'pinia'
import api from '@/services/axiosApi'
import * as jsondiffpatch from 'jsondiffpatch'
import { Team } from '@/types/team'

export const useTeamStore = defineStore('team', () => {
  const doesOwnCurrentTeam = ref(false) as Ref<boolean>
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const router = useRouter()
  const { currentTeam, currentTeamPid } = storeToRefs(configStore)
  const { user } = storeToRefs(authStore)

  // Detect if we're in demo mode
  const isDemo = computed(() => currentTeamPid.value === 'demo')

  // Query for authenticated user's real teams - NEVER affected by demo mode
  // This prevents race conditions during registration where FakeLayout sets demo mode
  // but we need to fetch the user's actual teams for post-login redirect
  const userTeamsQuery = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/api/teams').then((res) => res.data),
    enabled: computed(() => !!user.value)
  })

  // Separate query for demo teams - only used when in demo mode AND not authenticated
  const demoTeamsQuery = useQuery({
    queryKey: ['demo-teams'],
    queryFn: () => api.get('/api/demo/teams').then((res) => res.data),
    enabled: computed(() => isDemo.value && !user.value)
  })

  // Return real teams for authenticated users, demo teams only for unauthenticated demo viewers
  const teams = computed(() => {
    if (user.value) {
      return userTeamsQuery.data.value || []
    }
    return demoTeamsQuery.data.value || []
  })

  watch(
    () => currentTeam.value,
    (is, was) => {
      if (was) {
        const diff = jsondiffpatch.diff(was, is)
        if (diff) {
          const diffKeys = Object.keys(diff)
          const ignoredKeys = ['last_seen_at', 'owner', 'updated_at']

          // Check if all diff keys are in the ignored list
          const shouldIgnore = diffKeys.every((key) =>
            ignoredKeys.includes(key)
          )

          if (shouldIgnore) {
            return // Skip debounce if only ignored fields changed
          }
        }
        debouncedUpdateTeam(is)
      }
    },
    { deep: true }
  )

  const updateTeamMutation = useMutation({
    mutationFn: (variables: { team: Team }) =>
      api.put(`/api/${variables.team.pid}/team`, {
        ...variables.team
      }),
    onSuccess: (_, variables) => {
      // Track team settings update
      recordAnalyticsEvent(AnalyticsEventTypes.SETTINGS_UPDATED, {
        settingType: 'team',
        teamId: variables.team.pid,
        teamName: variables.team.name,
        updatedFields: Object.keys(variables.team)
      })
    }
  })

  const deleteTeamMutation = useMutation({
    mutationFn: (variables: { team: Team }) =>
      api.delete(`/api/teams/${variables.team.pid}`),
    onSuccess: (_, variables) => {
      userTeamsQuery.refetch()
      configStore.configQuery.refetch()
      authStore.getUser(true)

      // Track team deletion
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_DELETED, {
        teamId: variables.team.pid,
        teamName: variables.team.name
      })
    }
  })

  const removeTeamMemberMutation = useMutation({
    mutationFn: (variables: { memberId: number; isInvited: boolean }) => {
      return api.delete(
        `api/${currentTeamPid.value}/team/members/${variables.memberId}`,
        {
          params: {
            invited: variables.isInvited ? '1' : '0'
          }
        }
      )
    },
    onSuccess: (_, variables) => {
      configStore.configQuery.refetch()
      
      // Track team member removal
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_REMOVED, {
        teamId: currentTeamPid.value,
        memberId: variables.memberId,
        wasInvited: variables.isInvited
      })
    }
  })

  const addTeamMemberMutation = useMutation({
    mutationFn: (memberData: { email: string; role: string }) =>
      api.post(`/api/${currentTeamPid.value}/team/members`, memberData),
    onSuccess: (_, variables) => {
      configStore.configQuery.refetch()
      
      // Track team member addition
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_MEMBER_ADDED, {
        teamId: currentTeamPid.value,
        memberEmail: variables.email,
        memberRole: variables.role
      })
    }
  })

  const deleteTeam = (team: Team) => {
    deleteTeamMutation.mutate({ team })
  }
  const removeTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } =
      removeTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }
  const addTeamMember = () => {
    const { isError, error, isPending, mutate, mutateAsync } =
      addTeamMemberMutation
    return { isPending, error, isError, mutate, mutateAsync }
  }

  const debouncedUpdateTeam = useDebounceFn((newTeam: Team) => {
    updateTeamMutation.mutate({ team: newTeam })
  }, 2000)

  const create = (team: Team) => {
    api.post('/api/teams', { ...team }).then(async ({ data }) => {
      await userTeamsQuery.refetch()

      // Track team creation
      recordAnalyticsEvent(AnalyticsEventTypes.TEAM_CREATED, {
        teamId: data.team.pid,
        teamName: team.name,
        teamWebsite: team.website
      })

      router.push({
        name: ROUTES.DASHBOARD,
        params: { teamPid: data.team.pid }
      })
    })
  }

  // Compute isSuccess based on which query is active
  const isSuccess = computed(() => {
    if (user.value) {
      return userTeamsQuery.isSuccess.value
    }
    return demoTeamsQuery.isSuccess.value
  })

  return {
    teams,
    currentTeam,
    doesOwnCurrentTeam,
    isDemo,
    isSuccess,
    deleteTeam,
    create,
    removeTeamMember,
    addTeamMember
  }
})
